import { Alert } from "@mui/material";
import Banner from "../components/BannerNew";
import Copyrights from "../components/Copyrights";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Styles from "../components/Styles";
import { Fragment, useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { commitTransaction, getReservationDetails } from "../utils/Hote";
import { sendHotelConfirmationEmail } from "./hotels/booking/HotelEmailService";
import HotelVoucherNew from "../components/hotel/HotelVoucherNew";

export const OkPaymentHotels = () => {
  const translationContext = useTranslation();
  return (
    <div>
      <Styles />
      <Header translation={translationContext.t} translationContext={translationContext}/>
      <Banner showSearchBar={false} minHeight="300px" />
      <HotelPaymentContent />
      <Footer />
      <Copyrights />
    </div>
  );
};

const HotelPaymentContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [voucherData, setVoucherData] = useState(null);
  const [reservationData, setReservationData] = useState(null);
  const [processingStep, setProcessingStep] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const processPaymentSuccess = async () => {
      try {
        // Get transactionId from URL parameters
        const transactionId = searchParams.get('transactionId') || 
                             searchParams.get('transaction_id') ||
                             searchParams.get('sessionId') ||
                             searchParams.get('session_id');
        
        const status = searchParams.get('status') || 'completed';
        const reservationNumber = searchParams.get('reservationNumber');

        if (!transactionId) {
          setError('Transaction ID not found in URL parameters');
          setLoading(false);
          return;
        }

        
        let finalReservationNumber = reservationNumber;
        
        // Only commit transaction if not already completed
        if (status !== 'completed' && !reservationNumber) {
            setProcessingStep('Committing reservation...');

          const commitResult = await commitTransaction(transactionId);
          
          if (!commitResult?.reservationNumber) {
            throw new Error('Failed to commit hotel reservation transaction');
          }

          finalReservationNumber = commitResult.reservationNumber;
        } else {
        }

        setProcessingStep('Retrieving reservation details...');

        // Step 2: Get reservation details
        if (!finalReservationNumber) {
          throw new Error('No reservation number available - transaction may not have been committed properly');
        }
        
        const reservationDetails = await getReservationDetails(finalReservationNumber);
        
        if (!reservationDetails || !reservationDetails.body) {
          throw new Error('Failed to retrieve hotel reservation details');
        }


        // Step 3: Store both raw reservation data and processed voucher data
        setReservationData(reservationDetails.body.reservationData);
        const processedVoucher = processVoucherData(reservationDetails.body);
        setVoucherData(processedVoucher);

        // Step 4: Send confirmation email using existing flight email service
        setProcessingStep('Sending confirmation email...');
        
        try {
          const hotelEmailData = {
            reservationNumber: finalReservationNumber,
            hotelName: processedVoucher.hotelName,
            city: processedVoucher.city,
            country: processedVoucher.country,
            checkIn: processedVoucher.checkIn,
            checkOut: processedVoucher.checkOut,
            email: processedVoucher.guests?.[0]?.address?.email || 
                   processedVoucher.guests?.find(g => g.isLeader)?.address?.email ||
                   searchParams.get('email'),
            firstName: processedVoucher.guests?.[0]?.name || 
                      processedVoucher.guests?.find(g => g.isLeader)?.name ||
                      searchParams.get('name') || 'Guest',
            lastName: processedVoucher.guests?.[0]?.surname || 
                     processedVoucher.guests?.find(g => g.isLeader)?.surname || '',
            totalPrice: processedVoucher.totalPrice,
            guests: processedVoucher.guests,
            transactionId: transactionId
          };

          
          const emailResult = await sendHotelConfirmationEmail(hotelEmailData);
          
          if (emailResult.success) {
            setEmailSent(true);
          } else {
            console.error('❌ Failed to send hotel confirmation email:', emailResult.error);
            // Don't fail the whole process if email fails
          }
        } catch (emailError) {
          console.error('❌ Error sending hotel confirmation email:', emailError);
          // Don't fail the whole process if email fails
        }

        setLoading(false);
        setProcessingStep('');

      } catch (err) {
        console.error('❌ Error processing hotel payment:', err);
        setError(err.message || 'An error occurred while processing your hotel reservation');
        setLoading(false);
        setProcessingStep('');
      }
    };

    processPaymentSuccess();
  }, [searchParams]);

  const processVoucherData = (reservationDetails) => {
    const { reservationData } = reservationDetails;
    const hotelService = reservationData?.services?.find(service => service.productType === 2);
    
    return {
      reservationNumber: reservationData?.reservationInfo?.bookingNumber,
      hotelName: hotelService?.name,
      hotelDetails: hotelService?.serviceDetails?.hotelDetail,
      checkIn: reservationData?.reservationInfo?.beginDate,
      checkOut: reservationData?.reservationInfo?.endDate,
      nights: hotelService?.serviceDetails?.night,
      room: hotelService?.serviceDetails?.room,
      board: hotelService?.serviceDetails?.board,
      guests: reservationData?.travellers || [],
      totalPrice: reservationData?.reservationInfo?.totalPrice,
      documents: reservationData?.reservationInfo?.documents || [],
      hotelAddress: hotelService?.serviceDetails?.hotelDetail?.address?.addressLines?.[0],
      city: hotelService?.serviceDetails?.hotelDetail?.city?.name,
      country: hotelService?.serviceDetails?.hotelDetail?.country?.name,
      notes: hotelService?.note,
      cancellationPolicies: hotelService?.cancellationPolicies || []
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return `${price.amount} ${price.currency}`;
  };



  if (loading) {
    return (
      <Fragment>
        <section className="content">
          <div className="container">
            <div className="row">
              <div className="col-lg-10 mt-lg-0 mt-md-0 mt-4 valuetabs col-md-8 col-12 whitebox">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minHeight: "300px",
                    justifyContent: "center",
                  }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">{t("loading")}</span>
                  </div>
                  <h5 className="mt-3">{processingStep || t("processing_reservation")}</h5>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Fragment>
    );
  }

  if (error) {
    return (
      <Fragment>
        <section className="content">
          <div className="container">
            <div className="row">
              <div className="col-lg-10 mt-lg-0 mt-md-0 mt-4 valuetabs col-md-8 col-12 whitebox">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minHeight: "300px",
                    justifyContent: "center",
                  }}
                >
                  <img src={require("../assets/img/red.png")} alt="error" />
                  <h5 className="text-danger mt-3">{t("Error")}</h5>
                  <p className="text-center">{error}</p>
                  <button
                    type="button"
                    onClick={() => navigate("/hotels")}
                    className="btn-submit default-yellow-btn mt-3"
                  >
                    {t("back_to_hotel_search")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <section className="content">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mt-lg-0 mt-md-0 mt-4 valuetabs col-md-8 col-12 whitebox">
              {/* Success Header */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginBottom: "30px",
                }}
              >
                <img src={require("../assets/img/green.png")} alt="success" />
                <h5 className="okPaymentFont">
                  {t("Confirmed")}
                </h5>
                <p className="text-center">
                  {t("Thank you for booking with us. Your hotel reservation has been confirmed.")}
                </p>
              </div>

              {/* Email confirmation status */}
              {emailSent && (
                <div className="alert alert-success d-flex align-items-center" role="alert">
                  <i className="fas fa-envelope-check me-2"></i>
                  <div>
                    <strong>Confirmation email sent!</strong> Check your inbox for your booking details and voucher.
                  </div>
                </div>
              )}

              {/* Hotel Voucher Details */}
              {voucherData && reservationData && (
                <div className="row">
                  <div className="col-12">
                    {/* Quick Summary Card */}
                    <div className="card shadow-sm mb-4" style={{ backgroundColor: 'white' }}>
                      <div className="card-header bg-primary text-white">
                        <h6 className="mb-0">
                          <i className="fas fa-hotel mr-2"></i>
                          {t("Booking Confirmation")}
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <p><strong>{t("confirmation_number")}:</strong> {voucherData.reservationNumber}</p>
                            <p><strong>{t("hotel")}:</strong> {voucherData.hotelName}</p>
                            <p><strong>{t("location")}:</strong> {voucherData.city}, {voucherData.country}</p>
                          </div>
                          <div className="col-md-6">
                            <p><strong>{t("check_in")}:</strong> {formatDate(voucherData.checkIn)}</p>
                            <p><strong>{t("check_out")}:</strong> {formatDate(voucherData.checkOut)}</p>
                            <p><strong>{t("total_amount")}:</strong> <span className="text-success font-weight-bold">{formatPrice(voucherData.totalPrice)}</span></p>
                          </div>
                        </div>
                          </div>
                        </div>

                    {/* Professional PDF Voucher Component */}
                    <div className="card shadow-sm">
                      <div className="card-header bg-success text-white">
                        <h6 className="mb-0">
                          <i className="fas fa-file-pdf mr-2"></i>
                          {t("Download Your Hotel Voucher")}
                        </h6>
                            </div>
                      <div className="card-body text-center">
                        <p className="mb-3">
                          {t("Your professional hotel voucher is ready for download. This document contains all your booking details and should be presented at the hotel during check-in.")}
                        </p>
                        
                        {/* New Professional Voucher Component */}
                        <HotelVoucherNew reservationData={reservationData} />
                        
                        <div className="mt-3">
                          <small className="text-muted">
                            <i className="fas fa-info-circle mr-1"></i>
                            {t("Please save this voucher and bring it with you during your trip.")}
                                </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Continue Search Button */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => navigate("/hotels")}
                  className="btn-submit default-yellow-btn okPaymentCustom"
                >
                  {t("Continue searching hotels")}
                  <img 
                    src={require("../assets/img/icon-search.png")} 
                    alt="search" 
                    width={25} 
                    height={25} 
                    className="ml-2"
                  />
                </button>
              </div>
            </div>

            <div className="col-lg-2 mt-lg-0 mt-md-4 col-md-6 ml-auto col-12">
              <div className="col-lg-12 logobox text-center bg-white col-md-12 col-12 sticky">
                <img
                  src={require("../assets/img/sims_marketing_agency_logo.png")}
                  alt="agency"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default OkPaymentHotels; 